import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ClosingScreen = ({ onRestart }) => {
  const [phase, setPhase] = useState(0);
  // Phase 0: Show final line lingering
  // Phase 1: Begin fade to black
  // Phase 2: Complete darkness / stillness
  // Phase 3: Subtle "begin again" becomes available (but not shown as button)

  useEffect(() => {
    const timings = [
      5000,   // Phase 0 -> 1: Linger on "Vaulten closes"
      4000,   // Phase 1 -> 2: Fade to complete black
      10000,  // Phase 2 -> 3: Stay in stillness, then allow restart
    ];

    if (phase < 3) {
      const timer = setTimeout(() => {
        setPhase(prev => prev + 1);
      }, timings[phase]);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  // Handle restart - only available in final phase
  const handleRestart = useCallback(() => {
    if (phase >= 2) {
      onRestart();
    }
  }, [phase, onRestart]);

  // Listen for any interaction in stillness phase
  useEffect(() => {
    if (phase >= 2) {
      const handleInteraction = () => {
        handleRestart();
      };

      window.addEventListener('click', handleInteraction);
      window.addEventListener('keydown', handleInteraction);
      window.addEventListener('touchend', handleInteraction);

      return () => {
        window.removeEventListener('click', handleInteraction);
        window.removeEventListener('keydown', handleInteraction);
        window.removeEventListener('touchend', handleInteraction);
      };
    }
  }, [phase, handleRestart]);

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center z-50"
      initial={{ backgroundColor: 'hsl(240, 6%, 4%)' }}
      animate={{ 
        backgroundColor: phase >= 1 ? 'hsl(240, 8%, 2%)' : 'hsl(240, 6%, 4%)'
      }}
      transition={{ duration: 4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Final content fades out */}
      <AnimatePresence>
        {phase === 0 && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-center px-6"
          >
            <p 
              className="font-display text-xl sm:text-2xl md:text-3xl italic"
              style={{ color: 'hsl(40, 18%, 82%)' }}
            >
              Vaulten closes.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Complete darkness - stillness */}
      {phase >= 2 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 1 }}
          className="absolute inset-0"
        />
      )}

      {/* Very subtle hint after extended stillness - barely visible */}
      <AnimatePresence>
        {phase >= 3 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.15 }}
            transition={{ duration: 3 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] uppercase font-body"
            style={{ color: 'hsl(40, 5%, 40%)' }}
          >
            begin again
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ClosingScreen;
